import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const WorkspaceBoardsSectionPrefetchDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"WorkspaceBoardsSectionPrefetch"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"id"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"organization"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"id"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"credits"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"count"}},{"kind":"Field","name":{"kind":"Name","value":"type"}},{"kind":"Field","name":{"kind":"Name","value":"via"}}]}},{"kind":"Field","name":{"kind":"Name","value":"displayName"}},{"kind":"Field","name":{"kind":"Name","value":"enterprise"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"idAdmins"}}]}},{"kind":"Field","name":{"kind":"Name","value":"enterpriseJoinRequest"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"idEnterprise"}}]}},{"kind":"Field","name":{"kind":"Name","value":"idEnterprise"}},{"kind":"Field","name":{"kind":"Name","value":"idEntitlement"}},{"kind":"Field","name":{"kind":"Name","value":"memberships"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"deactivated"}},{"kind":"Field","name":{"kind":"Name","value":"idMember"}},{"kind":"Field","name":{"kind":"Name","value":"memberType"}},{"kind":"Field","name":{"kind":"Name","value":"unconfirmed"}}]}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"offering"}},{"kind":"Field","name":{"kind":"Name","value":"paidAccount"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"standing"}},{"kind":"Field","name":{"kind":"Name","value":"trialExpiration"}}]}},{"kind":"Field","name":{"kind":"Name","value":"prefs"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"permissionLevel"}}]}},{"kind":"Field","name":{"kind":"Name","value":"premiumFeatures"}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"WorkspaceBoardsSectionPrefetch","document":WorkspaceBoardsSectionPrefetchDocument}} as const;
export type WorkspaceBoardsSectionPrefetchQueryVariables = Types.Exact<{
  id: Types.Scalars['ID']['input'];
}>;


export type WorkspaceBoardsSectionPrefetchQuery = (
  { __typename: 'Query' }
  & { organization?: Types.Maybe<(
    { __typename: 'Organization' }
    & Pick<Types.Organization, 'id' | 'displayName' | 'idEnterprise' | 'idEntitlement' | 'name' | 'offering' | 'premiumFeatures'>
    & { credits: Array<(
      { __typename: 'Credit' }
      & Pick<Types.Credit, 'id' | 'count' | 'type' | 'via'>
    )>, enterprise?: Types.Maybe<(
      { __typename: 'Enterprise' }
      & Pick<Types.Enterprise, 'id' | 'idAdmins'>
    )>, enterpriseJoinRequest?: Types.Maybe<(
      { __typename: 'EnterpriseJoinRequest' }
      & Pick<Types.EnterpriseJoinRequest, 'idEnterprise'>
    )>, memberships: Array<(
      { __typename: 'Organization_Membership' }
      & Pick<Types.Organization_Membership, 'id' | 'deactivated' | 'idMember' | 'memberType' | 'unconfirmed'>
    )>, paidAccount?: Types.Maybe<(
      { __typename: 'PaidAccount' }
      & Pick<Types.PaidAccount, 'standing' | 'trialExpiration'>
    )>, prefs: (
      { __typename: 'Organization_Prefs' }
      & Pick<Types.Organization_Prefs, 'permissionLevel'>
    ) }
  )> }
);

/**
 * __useWorkspaceBoardsSectionPrefetchQuery__
 *
 * To run a query within a React component, call `useWorkspaceBoardsSectionPrefetchQuery` and pass it any options that fit your needs.
 * When your component renders, `useWorkspaceBoardsSectionPrefetchQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useWorkspaceBoardsSectionPrefetchQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useWorkspaceBoardsSectionPrefetchQuery(
  baseOptions: TrelloQueryHookOptions<
    WorkspaceBoardsSectionPrefetchQuery,
    WorkspaceBoardsSectionPrefetchQueryVariables
  > &
    (
      | {
          variables: WorkspaceBoardsSectionPrefetchQueryVariables;
          skip?: boolean;
        }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: WorkspaceBoardsSectionPrefetchDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    WorkspaceBoardsSectionPrefetchQuery,
    WorkspaceBoardsSectionPrefetchQueryVariables
  >(WorkspaceBoardsSectionPrefetchDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useWorkspaceBoardsSectionPrefetchLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    WorkspaceBoardsSectionPrefetchQuery,
    WorkspaceBoardsSectionPrefetchQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    WorkspaceBoardsSectionPrefetchQuery,
    WorkspaceBoardsSectionPrefetchQueryVariables
  >(WorkspaceBoardsSectionPrefetchDocument, options);
}
export function useWorkspaceBoardsSectionPrefetchSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        WorkspaceBoardsSectionPrefetchQuery,
        WorkspaceBoardsSectionPrefetchQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    WorkspaceBoardsSectionPrefetchQuery,
    WorkspaceBoardsSectionPrefetchQueryVariables
  >(WorkspaceBoardsSectionPrefetchDocument, options);
}
export type WorkspaceBoardsSectionPrefetchQueryHookResult = ReturnType<
  typeof useWorkspaceBoardsSectionPrefetchQuery
>;
export type WorkspaceBoardsSectionPrefetchLazyQueryHookResult = ReturnType<
  typeof useWorkspaceBoardsSectionPrefetchLazyQuery
>;
export type WorkspaceBoardsSectionPrefetchSuspenseQueryHookResult = ReturnType<
  typeof useWorkspaceBoardsSectionPrefetchSuspenseQuery
>;
export type WorkspaceBoardsSectionPrefetchQueryResult = Apollo.QueryResult<
  WorkspaceBoardsSectionPrefetchQuery,
  WorkspaceBoardsSectionPrefetchQueryVariables
>;
